import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';

const emptyAddress = () => ({
  practice_company_name: '',
  street_with_number: '',
  zip: '',
  city: '',
  phone_numbers: '',
  website: '',
});

const ProfileEdit = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [biography, setBiography] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [cliniques, setCliniques] = useState([]);
  const [selectedCliniques, setSelectedCliniques] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [phone, setPhone] = useState('');
  const [addresses, setAddresses] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/backend/api/profile_get.php', { credentials: 'include' });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || 'Erreur');
        const p = data.data.profile;
        setBiography(p.biography || '');
        setContactEmail(p.contact_email || '');
        setPhone(p.phone || '');
        setProfilePictureUrl(p.profile_picture || '');
        setCliniques(data.data.cliniques || []);
        setSelectedCliniques(data.data.user_cliniques || []);
        setLanguages(data.data.languages || []);
        setSelectedLanguages(data.data.user_languages || []);
        setAddresses(
          (data.data.addresses || []).map((a) => ({
            practice_company_name: a.practice_company_name || '',
            street_with_number: a.street_with_number || '',
            zip: a.zip || '',
            city: a.city || '',
            phone_numbers: a.phone_numbers || '',
            website: a.website || '',
          }))
        );
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCliniqueChange = (id) => {
    setSelectedCliniques((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const handleLanguageChange = (id) => {
    setSelectedLanguages((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const updateAddress = (index, field, value) => {
    setAddresses((prev) => prev.map((a, i) => (i === index ? { ...a, [field]: value } : a)));
  };
  const addAddress = () => setAddresses((prev) => [...prev, emptyAddress()]);
  const removeAddress = (index) => setAddresses((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    try {
      const formData = new FormData();
      formData.append('biography', biography);
      formData.append('contact_email', contactEmail);
      if (profilePicture) formData.append('profile_picture', profilePicture);
      formData.append('cliniques', JSON.stringify(selectedCliniques));
      formData.append('languages', JSON.stringify(selectedLanguages));
      formData.append('phone', phone);
      formData.append('addresses', JSON.stringify(addresses));

      const res = await fetch('/backend/api/profile_update.php', { method: 'POST', body: formData, credentials: 'include' });
      const data = await res.json();
      if (data.success) setMessage('Profil mis à jour');
      else setError(data.message || 'Erreur');
    } catch (e) {
      setError('Erreur serveur');
    }
  };

  if (!user) {
    return (
      <Container className="my-5"><Alert variant="warning">Vous devez être connecté pour accéder à cette page.</Alert></Container>
    );
  }
  if (loading) {
    return <Container className="my-5"><div>Chargement…</div></Container>;
  }

  return (
    <Container className="my-5">
      <Row className="justify-content-center" style={{ marginTop: '100px' }}>
        <Col md={8}>
          <Card>
            <Card.Body>
              <Card.Title>Mon profil</Card.Title>
              {message && <Alert variant="success">{message}</Alert>}
              {error && <Alert variant="danger">{error}</Alert>}

              {profilePictureUrl && (
                <div className="mb-3">
                  <img
                    src={profilePictureUrl.startsWith('http') ? profilePictureUrl : profilePictureUrl.replace('./', '/')}
                    alt="Photo de profil"
                    style={{ maxWidth: '120px', borderRadius: '8px' }}
                  />
                </div>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Changer la photo de profil</Form.Label>
                <Form.Control type="file" accept="image/*" onChange={(e) => setProfilePicture(e.target.files[0])} />
              </Form.Group>

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Biographie</Form.Label>
                  <Form.Control as="textarea" rows={3} value={biography} onChange={(e) => setBiography(e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email du cabinet</Form.Label>
                  <Form.Control type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Téléphone</Form.Label>
                  <Form.Control type="text" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </Form.Group>

                {/* Gestionnaire d'adresses : affichées dans l'annuaire et sur la carte */}
                <hr />
                <h5>Mes adresses de cabinet</h5>
                <Alert variant="info" className="py-2">
                  Ce sont les adresses affichées dans l'annuaire et sur la carte. Vous pouvez corriger,
                  supprimer ou ajouter des adresses. <strong>Dès que vous enregistrez, vos adresses ne
                  sont plus écrasées par le registre fédéral</strong> — c'est vous qui les gérez.
                </Alert>

                {addresses.length === 0 && <p className="text-muted">Aucune adresse. Ajoutez-en une ci-dessous.</p>}

                {addresses.map((a, i) => (
                  <Card key={i} className="mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Adresse {i + 1}</strong>
                        <Button variant="outline-danger" size="sm" onClick={() => removeAddress(i)}>Supprimer</Button>
                      </div>
                      <Form.Group className="mb-2">
                        <Form.Label>Nom du cabinet / établissement</Form.Label>
                        <Form.Control value={a.practice_company_name} onChange={(e) => updateAddress(i, 'practice_company_name', e.target.value)} />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Rue et numéro</Form.Label>
                        <Form.Control value={a.street_with_number} onChange={(e) => updateAddress(i, 'street_with_number', e.target.value)} />
                      </Form.Group>
                      <Row>
                        <Col xs={4}>
                          <Form.Group className="mb-2">
                            <Form.Label>NPA</Form.Label>
                            <Form.Control value={a.zip} onChange={(e) => updateAddress(i, 'zip', e.target.value)} />
                          </Form.Group>
                        </Col>
                        <Col>
                          <Form.Group className="mb-2">
                            <Form.Label>Ville</Form.Label>
                            <Form.Control value={a.city} onChange={(e) => updateAddress(i, 'city', e.target.value)} />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-2">
                        <Form.Label>Téléphone du cabinet</Form.Label>
                        <Form.Control value={a.phone_numbers} onChange={(e) => updateAddress(i, 'phone_numbers', e.target.value)} />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Site web</Form.Label>
                        <Form.Control type="url" placeholder="https://…" value={a.website} onChange={(e) => updateAddress(i, 'website', e.target.value)} />
                      </Form.Group>
                    </Card.Body>
                  </Card>
                ))}

                <Button variant="outline-primary" size="sm" className="mb-3" onClick={addAddress} type="button">
                  + Ajouter une adresse
                </Button>
                <hr />

                <Form.Group className="mb-3">
                  <Form.Label>Agréments cliniques</Form.Label>
                  {cliniques.map((c) => (
                    <Form.Check key={c.id} type="checkbox" label={c.name} checked={selectedCliniques.includes(c.id)} onChange={() => handleCliniqueChange(c.id)} />
                  ))}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Langues parlées</Form.Label>
                  {languages.map((l) => (
                    <Form.Check key={l.id} type="checkbox" label={l.name_fr} checked={selectedLanguages.includes(l.id)} onChange={() => handleLanguageChange(l.id)} />
                  ))}
                </Form.Group>

                <div className="d-flex justify-content-end">
                  <Button type="submit" variant="primary">Enregistrer</Button>
                </div>
                <br />
                {message && <Alert variant="success">{message}</Alert>}
                {error && <Alert variant="danger">{error}</Alert>}
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfileEdit;
